"use client";

import { IResource, WorkplaceRequestLocationValue } from "@gettactic/api";
import { Button } from "@gettactic/components/src/primitives/button";
import { useFloorsForOffices, useResource } from "@gettactic/hooks";
import { Pencil } from "lucide-react";
import React, { useRef, useState } from "react";
import { MapViewEmbedded } from "@/features/map/components/MapView/MapViewEmbedded";
import { StaticMapPinLayer } from "@/features/map/components/MapView/StaticMapPinLayer";
import { ResourceFinderModal } from "@/features/map/components/modal/ResourceFinderModal";

interface RequestLocationFieldProps {
  /** Current ticket-level location, or `null` when none is set. */
  value: WorkplaceRequestLocationValue | null;
  onChange: (next: WorkplaceRequestLocationValue | null) => void;
  /**
   * The resource behind a freshly picked `resource` location. The value only
   * carries the id; a form that wants to show the resource's day needs its
   * name, office and type too.
   */
  onResourcePicked?: (resource: IResource) => void;
  /** Office the request is filed under; scopes the resource finder and floor list. */
  officeId?: string;
  /** Office display name, for the preview footer. */
  officeName?: string;
  /**
   * Resource the value was prefilled from (map "file a request" flow). Lets the
   * selection show the resource's name/floor before the finder is ever opened.
   */
  prefilledResource?: { id: string; name: string; floorName?: string };
  /**
   * When true (the wizard was opened from the map against a specific resource or
   * pin), the location is fixed: render a read-only preview with no picker.
   */
  locked?: boolean;
  /**
   * The selected request type requires a real desk/room (not deferred to
   * approval time) — restricts the finder to the "Desk / room" mode only
   * (no Floor/Point) and flags an existing non-resource value as invalid.
   */
  requiresWorkspace?: boolean;
}

/**
 * Ticket-level location field shown in the request wizard sidebar. Shows the current
 * location (map preview + label) and a button that opens the resource finder — which
 * owns the Desk / Floor / Point mode toggle and the actual picking.
 */
export const RequestLocationField: React.FC<RequestLocationFieldProps> = ({
  value,
  onChange,
  onResourcePicked,
  officeId,
  officeName,
  prefilledResource,
  locked = false,
  requiresWorkspace = false
}) => {
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isFinderOpen, setIsFinderOpen] = useState(false);

  // Resolve the value's own element (a floor is an "area" resource), so floor and
  // point locations can recover their office from the picked floor even before the
  // request's office prop is set — otherwise the preview map could never load.
  const lookupId =
    value?.type === "resource"
      ? value.resource_id
      : value?.type === "floor" || value?.type === "coordinates"
        ? value.floor_id
        : null;
  const { resource: resolved } = useResource(lookupId);

  // Prefer the resolved element's own office; fall back to the request's office.
  const resolvedOfficeId = resolved?.office_id ?? officeId ?? null;

  const floorsQuery = useFloorsForOffices(
    resolvedOfficeId ? [resolvedOfficeId] : []
  );
  const floors = floorsQuery.data ?? [];

  const clear = () => onChange(null);

  const point = value?.type === "coordinates" ? value : null;

  // Fall back to the prefilled resource's name when the current value still points
  // at it, then to the resolved resource.
  const resourceLabel =
    (value?.type === "resource" && prefilledResource?.id === value.resource_id
      ? prefilledResource.name
      : null) ??
    resolved?.label ??
    resolved?.name ??
    "Selected resource";

  const previewFloorId =
    value?.type === "coordinates" || value?.type === "floor"
      ? value.floor_id
      : (resolved?.parent_id ?? null);
  const previewOfficeId =
    value?.type === "resource"
      ? (resolved?.office_id ?? null)
      : resolvedOfficeId;
  const previewFloorName =
    (value?.type === "resource" && prefilledResource?.id === value.resource_id
      ? prefilledResource.floorName
      : null) ?? floors.find((f) => f.id === previewFloorId)?.name;

  const selectedLabel =
    value?.type === "resource"
      ? resourceLabel
      : value?.type === "coordinates"
        ? "Pinned location"
        : value?.type === "floor"
          ? (floors.find((f) => f.id === value.floor_id)?.label ??
            floors.find((f) => f.id === value.floor_id)?.name ??
            "Selected floor")
          : null;

  const hasPreview = !!previewOfficeId && !!previewFloorId;

  const mapPreview = hasPreview ? (
    <div className="relative h-32 w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
      {point ? (
        <div ref={mapWrapperRef} className="relative h-full w-full">
          <MapViewEmbedded
            officeId={previewOfficeId as string}
            floorId={previewFloorId as string}
            focusPoint={{ x: point.x, y: point.y }}
            enableControls={false}
            showMiniMap={false}
            focusZoom={0.2}
          />
          <StaticMapPinLayer
            mapWrapperRef={mapWrapperRef}
            point={{ x: point.x, y: point.y }}
          />
        </div>
      ) : (
        <MapViewEmbedded
          officeId={previewOfficeId as string}
          floorId={previewFloorId as string}
          focussedResourceId={
            value?.type === "resource" ? value.resource_id : undefined
          }
          enableControls={false}
          showMiniMap={false}
          focusZoom={0.2}
        />
      )}
    </div>
  ) : value ? (
    <div className="flex h-32 w-full items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-400 text-xs">
      Loading map…
    </div>
  ) : null;

  const selectionSummary = value ? (
    <div className="min-w-0">
      <div className="truncate font-inter font-semibold text-[13px] text-neutral-900">
        {selectedLabel}
      </div>
      {[officeName, previewFloorName].filter(Boolean).length > 0 ? (
        <div className="mt-px truncate font-inter text-[12px] text-neutral-400">
          {[officeName, previewFloorName].filter(Boolean).join(" · ")}
        </div>
      ) : null}
    </div>
  ) : null;

  // A location carried over from before the type was picked (or changed) can
  // be a floor/pin that no longer satisfies a type that now requires a real
  // workspace — flag it rather than silently letting it through.
  const needsWorkspaceFix =
    requiresWorkspace && !!value && value.type !== "resource";

  // Read-only: the request was filed from the map against a fixed resource / pin.
  if (locked) {
    return (
      <div className="border-neutral-200 border-t pt-4">
        <div className="mb-2 font-bold font-inter text-[10px] text-neutral-500 uppercase tracking-[0.06em]">
          {value?.type === "resource" ? "For resource" : "For location"}
        </div>
        {mapPreview ? <div className="mb-2">{mapPreview}</div> : null}
        {selectionSummary}
        {needsWorkspaceFix ? (
          <p className="mt-1 font-inter text-[12px] text-semantic-error-foreground">
            This request needs a real desk or room — go back and pick a
            different type, or file it directly against a desk.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="border-neutral-200 border-t pt-4">
      <div className="mb-2 flex items-center gap-1 font-bold font-inter text-[10px] text-neutral-500 uppercase tracking-[0.06em]">
        Location
        {requiresWorkspace ? (
          <span className="text-semantic-error-foreground">*</span>
        ) : null}
      </div>

      {mapPreview ? <div className="mb-2">{mapPreview}</div> : null}

      {value ? (
        <div className="flex items-center justify-between gap-2">
          {selectionSummary}
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => setIsFinderOpen(true)}
              className="h-8 px-2 text-neutral-500 hover:text-neutral-900"
            >
              Change
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clear}
              className="h-8 px-2 text-neutral-400 hover:text-red-600"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="mb-1 font-inter text-[13px] text-neutral-400">
            {requiresWorkspace
              ? "This request needs a desk or room."
              : "No location attached."}
          </p>
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0 text-primary"
            onClick={() => setIsFinderOpen(true)}
          >
            {requiresWorkspace ? "Choose a desk or room" : "Choose on map"}
          </Button>
        </div>
      )}

      {needsWorkspaceFix ? (
        <p className="mt-1 font-inter text-[12px] text-semantic-error-foreground">
          A floor or pinned point isn't enough here — pick an actual desk or
          room.
        </p>
      ) : null}

      <ResourceFinderModal
        isOpen={isFinderOpen}
        onClose={() => setIsFinderOpen(false)}
        initialOfficeId={officeId}
        // Only pin the finder to a fixed office when the request already has one;
        // otherwise let the finder surface its own office selector so the user can
        // pick a location before a request-type default resolves the office.
        lockOffice={!!officeId}
        // A type that requires a workspace can only ever be satisfied by a real
        // desk/room, so skip the Desk/Floor/Point toggle entirely and go
        // straight to resource picking — mirrors the same restriction on the
        // approval-time finder in Sidebar.tsx.
        enableLocationModes={!requiresWorkspace}
        initialLocationMode={value?.type ?? "resource"}
        onSelect={(resource: IResource) => {
          onChange({ type: "resource", resource_id: resource.id });
          onResourcePicked?.(resource);
          setIsFinderOpen(false);
        }}
        onSelectLocation={(location: WorkplaceRequestLocationValue) => {
          onChange(location);
          setIsFinderOpen(false);
        }}
      />
    </div>
  );
};
